/**
 * Remedies.
 *
 * The owner executes. An agent requests, and the request becomes an owner inbox item that
 * carries the amount, the reason and the agent's name. That is the two person control a one
 * owner shop otherwise never gets, and it is why the button text differs by role instead of
 * the button being hidden: an agent should still be able to promise "I have asked for a
 * credit" and mean it.
 */
import { useState } from 'react';
import type { Role } from '@elutide/shared';
import { Card, buttonStyle } from '@/features/customers/temp-ui';
import { useRemedyRequest } from './api';

const KINDS = [
  { kind: 'reship' as const, label: 'Reship' },
  { kind: 'rr_credit' as const, label: 'Research Rewards credit' },
  { kind: 'refund' as const, label: 'Refund' },
  { kind: 'comp' as const, label: 'Comp' },
];

export function RemedyPanel({
  ticketRef,
  role,
  orderId,
}: {
  ticketRef: string;
  role: Role;
  orderId: number | null;
}) {
  const isOwner = role === 'owner';
  const request = useRemedyRequest(ticketRef);
  const [open, setOpen] = useState<(typeof KINDS)[number]['kind'] | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  return (
    <Card title={isOwner ? 'Remedies' : 'Ask the owner'}>
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        {KINDS.map((entry) => (
          <button key={entry.kind} type="button" style={buttonStyle()} onClick={() => setOpen(entry.kind)}>
            {isOwner ? entry.label : `Request ${entry.label.toLowerCase()}`}
          </button>
        ))}
      </div>

      {sent ? (
        <p role="status" style={{ color: 'var(--ok)', marginTop: 'var(--space-2)' }}>
          {sent}
        </p>
      ) : null}

      {open ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            request.mutate(
              {
                kind: open,
                amount_cents: Number(data.get('amount_cents') ?? 0),
                reason: String(data.get('reason') ?? ''),
                order_id: orderId,
              },
              {
                onSuccess: () => {
                  setSent(
                    isOwner
                      ? 'Recorded. Execute it from Orders or the customer profile.'
                      : 'Sent to the owner for approval.',
                  );
                  setOpen(null);
                },
              },
            );
          }}
          style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}
        >
          <label>
            <span className="caps-label">Amount in cents</span>
            <input name="amount_cents" type="number" min={0} defaultValue={0} style={{ width: '100%', padding: 'var(--space-2)' }} />
          </label>
          <label>
            <span className="caps-label">Reason</span>
            <input name="reason" required minLength={3} style={{ width: '100%', padding: 'var(--space-2)' }} />
          </label>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button type="button" style={buttonStyle()} onClick={() => setOpen(null)}>
              Cancel
            </button>
            <button type="submit" style={buttonStyle('primary')} disabled={request.isPending}>
              {isOwner ? 'Record' : 'Send request'}
            </button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
